/**
 * Prompt Test Page
 * Test prompts across multiple models and compare responses with latency
 */

import { useState, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Grid,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Stack,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Tooltip,
  Alert
} from '@mui/material';
import {
  PlayArrow as PlayArrowIcon,
  Clear as ClearIcon,
  ContentCopy as ContentCopyIcon,
  Speed as SpeedIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Psychology as PsychologyIcon,
  Close as CloseIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { toast } from 'sonner';
import { ApexOptions } from 'apexcharts';
import Chart from "react-apexcharts";
import { useInstances } from '../../hooks/useInstance';
import { modalCompareService } from '../../services/ModalCompareService';

interface ModelResponse {
  instance_id: string;
  model_name: string;
  response: string;
  latency: number;
  status: 'loading' | 'success' | 'error';
  error?: string;

  tokens?: {
    input: number;
    output: number;
    total: number;
  };

  cost?: {
    input: number;
    output: number;
    total: number;
  };

  confidence?:{
    avg_logprob: number;
    perplexity: number;
    score: number;
  }
}

interface EnhancedMetricsProps {
  response: ModelResponse;
  copyToClipboard: (text: string) => void;
}

function MetricsGrid({ response, copyToClipboard }: EnhancedMetricsProps) {
  return (
    <>
      {/* Copy Button */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Tooltip title="Copy response">
          <IconButton
            size="small"
            onClick={() => copyToClipboard(response.response)}
            sx={{ color: '#64748b' }}
          >
            <ContentCopyIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Enhanced Metrics Grid */}
      <Box
        sx={{
          display: 'flex',
          gap: 0,
          mt: 2,
          border: '1px solid #e2e8f0',
          borderRadius: 2,
          overflow: 'hidden',
          bgcolor: '#fff'
        }}
      >
        {/* Latency Card */}
        <Box
          sx={{
            flex: 1,
            p: 2.5,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            borderRight: '1px solid #e2e8f0',
            transition: 'all 0.2s ease',
            '&:hover': {
              bgcolor: '#f8fafc'
            }
          }}
        >
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '8px',
              bgcolor: '#e0f2fe',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <SpeedIcon sx={{ fontSize: 20, color: '#0284c7' }} />
          </Box>
          <Box>
            <Typography
              variant="caption"
              sx={{
                fontWeight: 600,
                color: '#64748b',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                fontSize: '0.688rem',
                display: 'block',
                mb: 0.3
              }}
            >
              Latency
            </Typography>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: '#0f172a',
                fontSize: '1.125rem'
              }}
            >
              {response.latency}s
            </Typography>
          </Box>
        </Box>

        {/* Tokens Card */}
        {response.tokens && (
          <Box
            sx={{
              flex: 1,
              p: 2.5,
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              borderRight: '1px solid #e2e8f0',
              transition: 'all 0.2s ease',
              '&:hover': {
                bgcolor: '#f8fafc'
              }
            }}
          >
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '8px',
                bgcolor: '#fce7f3',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '20px',
                fontWeight: 700,
                color: '#db2777'
              }}
            >
              T
            </Box>
            <Box>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 600,
                  color: '#64748b',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  fontSize: '0.688rem',
                  display: 'block',
                  mb: 0.3
                }}
              >
                Tokens
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: '#0f172a',
                  fontSize: '1.125rem'
                }}
              >
                {response.tokens.total?.toLocaleString() || 0}
              </Typography>
            </Box>
          </Box>
        )}

        {/* Cost Card */}
        {response.cost && (
          <Box
            sx={{
              flex: 1,
              p: 2.5,
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              transition: 'all 0.2s ease',
              '&:hover': {
                bgcolor: '#f8fafc'
              }
            }}
          >
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '8px',
                bgcolor: '#dbeafe',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '20px',
                fontWeight: 700,
                color: '#0284c7'
              }}
            >
              $
            </Box>
            <Box>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 600,
                  color: '#64748b',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  fontSize: '0.688rem',
                  display: 'block',
                  mb: 0.3
                }}
              >
                Cost
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: '#0f172a',
                  fontSize: '1.125rem'
                }}
              >
                ${response.cost.total?.toFixed(6) || '0.000000'}
              </Typography>
            </Box>
          </Box>
        )}
      </Box>
    </>
  );
}

export function ModalComparePage() {
  const { instances } = useInstances();
  
  const [prompt, setPrompt] = useState('');
  const [selectedModelIds, setSelectedModelIds] = useState<string[]>(['', '']);
  const [responses, setResponses] = useState<ModelResponse[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Get available models for each slot
  const getAvailableModels = useCallback((slotIndex: number) => {
    const otherSelectedIds = selectedModelIds.filter((id, idx) => idx !== slotIndex && id !== '');
    return instances.filter(instance => {
      const provider = instance?.instance_config?.provider;
      return provider; // !otherSelectedIds.includes(instance.instance_id) && provider !== 'ollama';
    });
  }, [selectedModelIds, instances]);

  // Handle model selection
  const handleModelSelect = useCallback((slotIndex: number, instance_id: string) => {
    setSelectedModelIds(prev => {
      const newIds = [...prev];
      newIds[slotIndex] = instance_id;
      return newIds;
    });
  }, []);

  // Clear a model slot
  const clearModelSlot = useCallback((slotIndex: number) => {
    setSelectedModelIds(prev => {
      const newIds = [...prev];
      newIds[slotIndex] = '';
      return newIds;
    });
  }, []);


  // Map backend results to UI format
  const mapCompareResults = useCallback((results: Array<{ url: string; response: string; response_time_sec: number; tokens?: any; cost?: any; confidence?: any }>): ModelResponse[] => {
    // Helper to normalize URLs for comparison
    const normalizeUrl = (url: string) => url.replace(/\/$/, '');
    return results.map(({ url, response, response_time_sec, tokens, cost, confidence }) => {
      const normalizedUrl = normalizeUrl(url);
      const instance = instances.find(i => normalizeUrl(i.end_point) === normalizedUrl);
      const isError = response.startsWith('Error:');
      // Try to extract model name from response if instance is not found
      let modelName = instance?.instance_name;
      if (!modelName && response) {
        const match = response.match(/response from ([^\.]+)\./);
        if (match) modelName = match[1];
      }
      return {
        instance_id: instance?.instance_id || url,
        model_name: modelName || 'Unknown',
        response,
        latency: response_time_sec,
        status: isError ? 'error' : 'success',
        tokens: tokens,
        cost: cost,
        confidence: confidence,
        error: isError ? response : undefined
      };
    });
  }, [instances]);

  // Submit prompt to all selected models
  const handleSubmit = useCallback(async () => {
    if (!prompt.trim()) {
      toast.error('Please enter a prompt');
      return;
    }
    const selectedIds = selectedModelIds.filter(id => id !== '');
    if (selectedIds.length === 0) {
      toast.error('Please select at least one model');
      return;
    }

    setIsSubmitting(true);

    // Show loading state for selected models
    setResponses(selectedIds.map(instance_id => {
      const instance = instances.find(i => i.instance_id === instance_id);
      return {
        instance_id,
        model_name: instance?.instance_name || 'Unknown',
        response: '',
        latency: 0,
        status: 'loading'
      };
    }));

    try {
      // Prepare payload for backend
      const instance_urls = selectedIds.map(id => {
        const instance = instances.find(i => i.instance_id === id);
        return {
          url: instance?.end_point || '',
          provider: instance?.instance_config?.provider || '',
          models: instance?.instance_config?.model_name || ''
        };
      });
      const payload = { prompt, instance_urls };
      const { results } = await modalCompareService.compareModal(payload);
      console.log('results', results);
      setResponses(mapCompareResults(results));
      toast.success('All models responded!');
    } catch (error) {
      setResponses(selectedIds.map(instance_id => ({
        instance_id,
        model_name: instances.find(i => i.instance_id === instance_id)?.instance_name || 'Unknown',
        response: '',
        latency: 0,
        status: 'error',
        error: 'Failed to get response from models'
      })));
      toast.error('Failed to get responses');
    }
    setIsSubmitting(false);
  }, [prompt, selectedModelIds, instances, mapCompareResults]);

  // Copy response to clipboard
  const copyToClipboard = useCallback((text: string) => {
    // Try modern clipboard API first
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text)
        .then(() => {
          toast.success('Copied to clipboard');
        })
        .catch(() => {
          // Fallback to older method
          fallbackCopyToClipboard(text);
        });
    } else {
      // Use fallback for browsers that don't support clipboard API
      fallbackCopyToClipboard(text);
    }
  }, []);

  // Fallback copy method
  const fallbackCopyToClipboard = (text: string) => {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    
    try {
      document.execCommand('copy');
      toast.success('Copied to clipboard');
    } catch (err) {
      toast.error('Failed to copy to clipboard');
    }
    
    document.body.removeChild(textArea);
  };

  // Clear all
  const handleClearAll = useCallback(() => {
    setPrompt('');
    setSelectedModelIds(['', '', '']);
    setResponses([]);
  }, []);

  // Count selected models
  const selectedCount = selectedModelIds.filter(id => id !== '').length;

    const successfulResponses = responses.filter(
    r => r.status === 'success'
  );

  const chartCategories = successfulResponses.map(
    r => r.model_name
  );

  const series = [
    {
      name: 'Confidence Score',
      data: successfulResponses.map(r => r.confidence?.score ?? 0),
    }
  ];

const categories = successfulResponses.map(r => r.model_name);

  // Generate colors for each model bar - alternating between purple and cyan gradients
  const getBarColors = () => {
    return successfulResponses.map((_, index) => {
      // Alternate between two complementary gradients
      return index % 2 === 0 ? '#667eea' : '#4facfe'; // Purple and Cyan-light (gradient start)
    });
  };

  const getBarGradientColors = () => {
    return successfulResponses.map((_, index) => {
      return index % 2 === 0 ? '#764ba2' : '#00f2fe'; // Purple-end and Cyan-bright (gradient end)
    });
  };

  const chartOptions: ApexOptions = {
    chart: {
      type: "bar",
      toolbar: {
        show: true,
        tools: {
          download: true,
          reset: true,
        },
      },
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: "55%",
        borderRadius: 5,
        borderRadiusApplication: "end" as const,
        distributed: true,
      },
    },
    dataLabels: {
      enabled: false,
    },
    stroke: {
      show: true,
      width: 2,
      colors: ["transparent"],
    },
    xaxis: {
      categories,
      labels: {
        style: {
          fontWeight: 700,
          fontSize: '0.938rem',
          fontFamily: 'inherit',
        }
      }
    },
    yaxis: {
      labels: {
        style: {
          fontWeight: 400,
          fontSize: '0.938rem',
          fontFamily: 'inherit',
        }
      }
    },
    legend: {
      show: false, 
    },
    fill: {
      type: "gradient",
      gradient: {
        shade: "light",
        type: "vertical",
        shadeIntensity: 0.5,
        gradientToColors: getBarGradientColors(),
        inverseColors: false,
        opacityFrom: 1,
        opacityTo: 0.9,
        stops: [0, 100],
      },
    },
    colors: getBarColors(),
  };


  return (
    <Box sx={{ bgcolor: '#f8f9fa', minHeight: '100vh' }}>
      <Container maxWidth="xl" sx={{ py: 4 }}>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <PsychologyIcon sx={{ fontSize: 40, color: '#2563eb' }} />
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 800, color: '#0f172a' }}>
                  Models Compare
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Test your prompts across multiple models and compare responses
                </Typography>
              </Box>
            </Box>

            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={handleClearAll}
              disabled={!prompt && selectedCount === 0 && responses.length === 0}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                borderColor: '#e2e8f0',
                color: '#64748b',
                '&:hover': {
                  borderColor: '#cbd5e1',
                  bgcolor: '#f8fafc'
                }
              }}
            >
              Clear All
            </Button>
          </Box>
        </Box>

        {/* Input Section */}
        <Paper 
          elevation={0} 
          sx={{ 
            p: 3, 
            mb: 4, 
            borderRadius: 3, 
            border: '1px solid #e2e8f0',
            bgcolor: '#fff'
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f172a', mb: 2 }}>
            Enter Your Prompt
          </Typography>
          
          <TextField
            fullWidth
            multiline
            rows={6}
            placeholder="Type your prompt here... (e.g., 'Explain quantum computing in simple terms')"
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            sx={{
              '& .MuiOutlinedInput-root': {
                bgcolor: '#fafbfc',
                fontSize: '0.938rem',
                '& fieldset': {
                  borderColor: '#e2e8f0',
                },
                '&:hover fieldset': {
                  borderColor: '#cbd5e1',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#2563eb',
                }
              }
            }}
          />

          <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="caption" color="text.secondary">
              {prompt.length} characters
            </Typography>
            {prompt && (
              <Button
                size="small"
                startIcon={<ClearIcon />}
                onClick={() => setPrompt('')}
                sx={{
                  textTransform: 'none',
                  color: '#64748b',
                  '&:hover': {
                    bgcolor: '#f8fafc'
                  }
                }}
              >
                Clear
              </Button>
            )}
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f172a', mb: 2 }}>
            Select Models to Test
          </Typography>

          <Box sx={{ display: 'flex', gap: 3 }}>
            {selectedModelIds.map((instance_id, index) => {
              const availableModels = getAvailableModels(index);
              return (
                <Card
                  key={index}
                  elevation={0}
                  sx={{
                    flex: 1,
                    overflow: 'hidden'
                  }}
                >
                  <Box sx={{ position: 'relative' }}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Model {index + 1}</InputLabel>
                      <Select
                        value={instance_id}
                        onChange={(e) => handleModelSelect(index, e.target.value)}
                        label={`Model ${index + 1}`}
                      >
                        <MenuItem value="">
                          <em>Select a model</em>
                        </MenuItem>
                        {availableModels.map(model => (
                          <MenuItem key={model.instance_id} value={model.instance_id}>
                            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {model.instance_name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {model.instance_config?.provider}
                              </Typography>
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    {instance_id && (
                      <IconButton
                        size="small"
                        onClick={() => clearModelSlot(index)}
                        sx={{
                          position: 'absolute',
                          right: 36,
                          top: '50%',
                          transform: 'translateY(-50%)',
                          color: '#64748b',
                          zIndex: 10
                        }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                </Card>
              );
            })}
          </Box>

          {/* Submit Button */}
          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
            <Button
              variant="contained"
              size="large"
              startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : <PlayArrowIcon />}
              onClick={handleSubmit}
              disabled={isSubmitting || !prompt.trim() || selectedCount !== 2}
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                px: 6,
                py: 1.5,
                borderRadius: 2,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #5568d3 0%, #6b3f94 100%)',
                  boxShadow: '0 6px 16px rgba(102, 126, 234, 0.5)',
                },
                '&:disabled': {
                  background: '#e2e8f0',
                  color: '#94a3b8'
                }
              }}
            >
              {isSubmitting ? 'Comparing Models...' : 'Compare Models'}
            </Button>
          </Box>
        </Paper>

        {/* Results Section */}
        {responses.length > 0 && (
          <>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f172a', mb: 3 }}>
              Results
            </Typography>

            <Box sx={{ display: 'flex', gap: 3, mb: 4 }}>
              {responses.map((response, index) => (
                <Box key={response.instance_id} sx={{ flex: 1 }}>
                  <Card
                    elevation={0}
                    sx={{
                      height: '100%',
                      border: '1px solid #e2e8f0',
                      borderRadius: 3,
                      position: 'relative',
                      overflow: 'visible'
                    }}
                  >
                    {/* Status Badge */}
                    <Box
                      sx={{
                        position: 'absolute',
                        top: -12,
                        right: 16,
                        zIndex: 1
                      }}
                    >
                      {response.status === 'loading' && (
                        <Chip
                          icon={<CircularProgress size={16} sx={{ color: '#fff !important' }} />}
                          label="Running"
                          size="small"
                          sx={{
                            bgcolor: '#f59e0b',
                            color: '#fff',
                            fontWeight: 700,
                            boxShadow: '0 2px 8px rgba(245, 158, 11, 0.3)'
                          }}
                        />
                      )}
                      {response.status === 'success' && (
                        <Chip
                          icon={<CheckCircleIcon />}
                          label={`${response.latency}s`}
                          size="small"
                          sx={{
                            bgcolor: '#16a34a',
                            color: '#fff',
                            fontWeight: 700,
                            boxShadow: '0 2px 8px rgba(22, 163, 74, 0.3)'
                          }}
                        />
                      )}
                      {response.status === 'error' && (
                        <Chip
                          icon={<ErrorIcon />}
                          label="Error"
                          size="small"
                          sx={{
                            bgcolor: '#dc2626',
                            color: '#fff',
                            fontWeight: 700,
                            boxShadow: '0 2px 8px rgba(220, 38, 38, 0.3)'
                          }}
                        />
                      )}
                    </Box>

                    <CardContent sx={{ p: 3 }}>
                      {/* Model Name */}
                      <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f172a', mb: 0.5 }}>
                        {response.model_name}
                      </Typography>
                      
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                        Model {index + 1}
                      </Typography>

                      <Divider sx={{ mb: 2 }} />

                      {/* Response */}
                      {response.status === 'loading' && (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4 }}>
                          <CircularProgress size={40} />
                        </Box>
                      )}

                      {response.status === 'success' && (
                        <>
                          <Box
                            sx={{
                              bgcolor: '#fafbfc',
                              p: 2,
                              borderRadius: 2,
                              border: '1px solid #e2e8f0',
                              minHeight: 150,
                              maxHeight: 300,
                              overflowY: 'auto',
                              mb: 2
                            }}
                          >
                            <Typography style={{ whiteSpace: "pre-wrap" }} variant="body2" sx={{ lineHeight: 1.7, color: '#334155' }}>
                              {response.response}
                            </Typography>
                          </Box>

                          {/*Metrics Grid */}
                          <MetricsGrid response={response} copyToClipboard={copyToClipboard} />
                        </>
                      )}

                      {response.status === 'error' && (
                        <Alert severity="error" sx={{ mt: 2 }}>
                          {response.error || 'Failed to get response'}
                        </Alert>
                      )}
                    </CardContent>
                  </Card>
                </Box>
              ))}
            </Box>

          </>
        )}

        {successfulResponses.length > 0 && (
          <Paper
            elevation={0}
            sx={{
              p: 4,
              mb: 4,
              borderRadius: 3,
              border: '1px solid #e2e8f0',
              bgcolor: '#fff'
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 3 }}>
              Confidence Score Comparison
            </Typography>

            <Chart
              height={350}
              width="100%"
              type="bar"
              series={series}
              options={chartOptions}
            />
          </Paper>
        )}




        {/* Empty State */}
        {responses.length === 0 && !isSubmitting && (
          <Paper
            elevation={0}
            sx={{
              p: 6,
              borderRadius: 3,
              border: '2px dashed #e2e8f0',
              bgcolor: '#fafbfc',
              textAlign: 'center'
            }}
          >
            <PsychologyIcon sx={{ fontSize: 64, color: '#cbd5e1', mb: 2 }} />
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#64748b', mb: 1 }}>
              No results yet
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Enter a prompt and select models to start testing
            </Typography>
          </Paper>
        )}
      </Container>
    </Box>
  );
}

export default ModalComparePage;
